import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { usePartnerAnalytics } from '@/hooks/usePartnerAnalytics';
import { usePartnerAnalyticsFilters } from '@/hooks/usePartnerAnalyticsFilters';
import { MetricCard } from '@/components/partners/analytics/MetricCard';
import { AnalyticsFilterPanel } from '@/components/partners/analytics/AnalyticsFilterPanel';
import { RatingTrendChart } from '@/components/partners/charts/RatingTrendChart';
import { ComplianceGauge } from '@/components/partners/charts/ComplianceGauge';
import { PerformanceFunnelChart } from '@/components/partners/charts/PerformanceFunnelChart';
import { ComplianceHeatmapChart } from '@/components/partners/charts/ComplianceHeatmapChart';
import { RatingComparisonChart } from '@/components/partners/charts/RatingComparisonChart';
import { RatingDistributionChart } from '@/components/partners/charts/RatingDistributionChart';
import { RiskTrendChart } from '@/components/partners/charts/RiskTrendChart';
import { ContractorListDialog } from '@/components/partners/analytics/ContractorListDialog';
import { RatingBreakdownDialog } from '@/components/partners/analytics/RatingBreakdownDialog';
import { PendingDocumentsDialog } from '@/components/partners/analytics/PendingDocumentsDialog';
import { ExpiringContractorsDialog } from '@/components/partners/analytics/ExpiringContractorsDialog';
import { ContractorDetailSheet } from '@/components/partners/analytics/ContractorDetailSheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DateRange } from 'react-day-picker';
import { Building2, Star, FileCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { RatingDisplay } from '@/components/partners/RatingDisplay';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export default function PartnerAnalytics() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [prequalStatus, setPrequalStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [ratingRange, setRatingRange] = useState<[number, number]>([0, 5]);
  
  const [contractorListOpen, setContractorListOpen] = useState(false);
  const [ratingBreakdownOpen, setRatingBreakdownOpen] = useState(false);
  const [pendingDocsOpen, setPendingDocsOpen] = useState(false);
  const [expiringOpen, setExpiringOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<PerformanceSummary | null>(null);

  const queryClient = useQueryClient();

  const {
    performanceSummary,
    documentMetrics,
    ratingTrends,
    isLoading,
  } = usePartnerAnalytics();

  const handleRefreshData = () => {
    queryClient.invalidateQueries({ queryKey: ['partner-analytics-performance'] });
    queryClient.invalidateQueries({ queryKey: ['partner-analytics-trends'] });
    queryClient.invalidateQueries({ queryKey: ['partner-analytics-documents'] });
    toast.success('Analytics data refreshed');
  };

  const filteredContractors = usePartnerAnalyticsFilters(performanceSummary, {
    dateRange,
    prequalStatus,
    searchQuery,
    ratingRange,
  });

  // Calculate metrics from filtered data - ONLY contractors with ratings
  const contractorsWithRatings = filteredContractors.filter(p => p.overall_rating !== null && p.overall_rating > 0);
  const contractorsWithSafetyRatings = filteredContractors.filter(p => p.safety_rating !== null && p.safety_rating > 0);
  const contractorsWithQualityRatings = filteredContractors.filter(p => p.quality_rating !== null && p.quality_rating > 0);
  const contractorsWithReliabilityRatings = filteredContractors.filter(p => p.reliability_rating !== null && p.reliability_rating > 0);

  const aggregateMetrics = {
    totalContractors: filteredContractors.length,
    averageRating: contractorsWithRatings.length > 0
      ? contractorsWithRatings.reduce((sum, p) => sum + p.overall_rating, 0) / contractorsWithRatings.length
      : 0,
    pendingDocuments: filteredContractors.reduce((sum, p) => sum + p.pending_documents, 0),
    expiringSoon: filteredContractors.filter(p => p.prequalification_status === 'expiring_soon').length,
    averageSafetyRating: contractorsWithSafetyRatings.length > 0
      ? contractorsWithSafetyRatings.reduce((sum, p) => sum + p.safety_rating, 0) / contractorsWithSafetyRatings.length
      : 0,
    averageQualityRating: contractorsWithQualityRatings.length > 0
      ? contractorsWithQualityRatings.reduce((sum, p) => sum + p.quality_rating, 0) / contractorsWithQualityRatings.length
      : 0,
    averageReliabilityRating: contractorsWithReliabilityRatings.length > 0
      ? contractorsWithReliabilityRatings.reduce((sum, p) => sum + p.reliability_rating, 0) / contractorsWithReliabilityRatings.length
      : 0,
  };

  const topPerformers = filteredContractors
    .filter(p => p.overall_rating)
    .sort((a, b) => (b.overall_rating || 0) - (a.overall_rating || 0))
    .slice(0, 10);

  const atRiskContractors = filteredContractors.filter(
    p => (p.overall_rating || 0) < 3 || p.pending_documents > 2 || p.rejected_documents > 0
  );

  const activeFilterCount = [
    dateRange?.from,
    prequalStatus !== 'all',
    searchQuery,
    ratingRange[0] > 0 || ratingRange[1] < 5,
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setDateRange(undefined);
    setPrequalStatus('all');
    setSearchQuery('');
    setRatingRange([0, 5]);
  };

  // Calculate total document metrics
  const totalDocuments = documentMetrics?.reduce((sum, d) => sum + d.total_count, 0) || 0;
  const totalVerified = documentMetrics?.reduce((sum, d) => sum + d.verified_count, 0) || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Partner Analytics</h1>
            <p className="text-muted-foreground">
              Comprehensive insights into contractor and vendor performance
            </p>
          </div>
          <Button 
            onClick={handleRefreshData} 
            variant="outline" 
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>

        <AnalyticsFilterPanel
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          prequalStatus={prequalStatus}
          onPrequalStatusChange={setPrequalStatus}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          ratingRange={ratingRange}
          onRatingRangeChange={setRatingRange}
          onClearFilters={handleClearFilters}
          activeFilterCount={activeFilterCount}
        />

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                title="Total Contractors"
                value={aggregateMetrics.totalContractors}
                icon={Building2}
                onClick={() => setContractorListOpen(true)}
              />
              <MetricCard
                title="Average Rating"
                value={aggregateMetrics.averageRating.toFixed(2)}
                icon={Star}
                onClick={() => setRatingBreakdownOpen(true)}
              />
              <MetricCard
                title="Pending Documents"
                value={aggregateMetrics.pendingDocuments}
                icon={FileCheck}
                onClick={() => setPendingDocsOpen(true)}
              />
              <MetricCard
                title="Expiring Soon"
                value={aggregateMetrics.expiringSoon}
                icon={AlertTriangle}
                onClick={() => setExpiringOpen(true)}
              />
            </div>

            <PerformanceFunnelChart contractors={filteredContractors} />

            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Average Ratings by Category</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Safety</span>
                    <RatingDisplay rating={aggregateMetrics.averageSafetyRating} size="sm" />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Quality</span>
                    <RatingDisplay rating={aggregateMetrics.averageQualityRating} size="sm" />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Reliability</span>
                    <RatingDisplay rating={aggregateMetrics.averageReliabilityRating} size="sm" />
                  </div>
                </CardContent>
              </Card>

              <ComplianceGauge
                verifiedCount={totalVerified}
                totalCount={totalDocuments}
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <ComplianceHeatmapChart 
                contractors={filteredContractors} 
                documentMetrics={documentMetrics || []} 
              />
              <RiskTrendChart contractors={filteredContractors} />
            </div>

            {ratingTrends && ratingTrends.length > 0 && (
              <RatingTrendChart data={ratingTrends} />
            )}

            <RatingDistributionChart contractors={filteredContractors} />

            <RatingComparisonChart contractors={filteredContractors} />

            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Top Performers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topPerformers?.slice(0, 5).map((contractor) => (
                      <div 
                        key={contractor.id} 
                        className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                        onClick={() => setSelectedContractor(contractor)}
                      >
                        <span className="text-sm font-medium truncate">{contractor.name}</span>
                        <RatingDisplay rating={contractor.overall_rating} size="sm" />
                      </div>
                    ))}
                    {(!topPerformers || topPerformers.length === 0) && (
                      <p className="text-sm text-muted-foreground">No ratings available yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>At-Risk Contractors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {atRiskContractors?.slice(0, 5).map((contractor) => (
                      <div 
                        key={contractor.id} 
                        className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                        onClick={() => setSelectedContractor(contractor)}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium truncate">{contractor.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {contractor.pending_documents} pending, {contractor.rejected_documents} rejected
                          </p>
                        </div>
                        {contractor.overall_rating && (
                          <RatingDisplay rating={contractor.overall_rating} size="sm" />
                        )}
                      </div>
                    ))}
                    {(!atRiskContractors || atRiskContractors.length === 0) && (
                      <p className="text-sm text-muted-foreground">No at-risk contractors</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        <ContractorListDialog
          open={contractorListOpen}
          onOpenChange={setContractorListOpen}
          contractors={filteredContractors}
        />
        <RatingBreakdownDialog
          open={ratingBreakdownOpen}
          onOpenChange={setRatingBreakdownOpen}
          contractors={filteredContractors}
        />
        <PendingDocumentsDialog
          open={pendingDocsOpen}
          onOpenChange={setPendingDocsOpen}
          contractors={filteredContractors}
        />
        <ExpiringContractorsDialog
          open={expiringOpen}
          onOpenChange={setExpiringOpen}
          contractors={filteredContractors}
        />
        <ContractorDetailSheet
          open={!!selectedContractor}
          onOpenChange={(open) => !open && setSelectedContractor(null)}
          contractor={selectedContractor}
        />
      </div>
    </DashboardLayout>
  );
}
